/**
 * The organization being worked on. Customers can belong to several (their own company, clients
 * that invited them); the choice is remembered in this browser per account, so another account
 * signing in here never starts in it (PHP-3702). Permissions come from Identity and only drive
 * what the UI offers — every API enforces them again.
 */
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { Lock } from "lucide-react";
import { Navigate, useLocation } from "react-router";

import { buttonClass, EmptyState, ErrorState, FullPageSpinner, LinkButton } from "@/components/ui";
import { permissionChecks, type PermissionChecks } from "@/lib/access";
import { identity, session } from "@/lib/api";
import { SUPPORT_EMAIL } from "@/lib/support";
import type { MyOrganization, ResourceGrant } from "@/lib/types";

const STORAGE_KEY = "cortexgrip.portal.organization";

// The old single value was shared by every account in this browser: never trust it again.
try {
  localStorage.removeItem(STORAGE_KEY);
} catch {
  /* storage unavailable */
}

/** The signed-in account (kept while an expired session waits for the password), or null. */
function accountId(): string | null {
  const s = session.getSnapshot();
  return s.status === "signed_in" || s.status === "expired" ? s.user.id : null;
}

function storageKey(): string | null {
  const id = accountId();
  return id ? `${STORAGE_KEY}.${id}` : null;
}

interface OrgContextValue extends PermissionChecks {
  organizations: MyOrganization[];
  current: MyOrganization;
  orgId: string;
  /** Held across the whole business (what `can` checks). */
  permissions: ReadonlySet<string>;
  /** Held on specific websites or domain folders only. */
  resourceGrants: readonly ResourceGrant[];
  permissionsLoading: boolean;
  switchTo: (organizationId: string) => void;
}

const OrgContext = createContext<OrgContextValue | null>(null);

const orgListeners = new Set<() => void>();

/**
 * The signed-in account's remembered business, readable outside React. It may no longer be theirs (removed,
 * business deleted): use it only after checking it against their list (see `usableOrganizationId`).
 */
export const selectedOrganization = {
  get(): string | null {
    const key = storageKey();
    if (!key) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(id: string): void {
    const key = storageKey();
    try {
      if (key) localStorage.setItem(key, id);
    } catch {
      /* private mode: the choice just isn't remembered */
    }
    for (const l of [...orgListeners]) l();
  },
  subscribe(listener: () => void): () => void {
    orgListeners.add(listener);
    // A different account signing in changes what is remembered.
    const unsubscribeSession = session.subscribe(listener);
    return () => {
      orgListeners.delete(listener);
      unsubscribeSession();
    };
  },
};

/** The remembered business if it is one of `organizations` and active, else null (PHP-3702). */
export function usableOrganizationId(
  remembered: string | null,
  organizations: readonly MyOrganization[] | undefined,
): string | null {
  const found = organizations?.find((o) => o.organization.id === remembered);
  return found && found.organization.status === "active" ? found.organization.id : null;
}

/** The account's businesses (shared with the business switcher: one request). */
export function useMyOrganizations() {
  return useQuery({ queryKey: ["organizations"], queryFn: identity.organizations });
}

export function useSelectedOrganizationId(): string | null {
  return useSyncExternalStore(selectedOrganization.subscribe, selectedOrganization.get);
}

export function rememberOrganization(id: string): void {
  selectedOrganization.set(id);
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const orgs = useMyOrganizations();
  const selected = useSelectedOrganizationId();

  const items = useMemo(
    () => (orgs.data?.items ?? []).filter((o) => o.organization.status !== "deleted"),
    [orgs.data],
  );
  // With nothing (or nothing usable) remembered, start in a business that is active, not a suspended one.
  const current =
    items.find((o) => o.organization.id === selected) ??
    items.find((o) => o.organization.status === "active") ??
    items[0];
  const orgId = current?.organization.id;

  const perms = useQuery({
    queryKey: ["permissions", orgId],
    queryFn: () => identity.permissions(orgId!),
    enabled: Boolean(orgId),
    staleTime: 30_000,
    // Someone else may change this person's role at any time: pick it up in an open tab too, so
    // the sidebar and the actions offered follow (each API enforces it immediately regardless).
    refetchInterval: 60_000,
  });

  // Remember the business we fell back to, so organization-scoped calls outside React agree with the UI.
  useEffect(() => {
    if (orgId && orgId !== selected) selectedOrganization.set(orgId);
  }, [orgId, selected]);

  const switchTo = useCallback((id: string) => selectedOrganization.set(id), []);

  const value = useMemo<OrgContextValue | null>(() => {
    if (!current || !orgId) return null;
    const set = new Set(perms.data?.permissions ?? []);
    const grants = perms.data?.resource_grants ?? [];
    return {
      organizations: items,
      current,
      orgId,
      permissions: set,
      resourceGrants: grants,
      permissionsLoading: perms.isPending,
      ...permissionChecks(set, grants),
      switchTo,
    };
  }, [current, orgId, items, perms.data, perms.isPending, switchTo]);

  if (orgs.isPending) return <FullPageSpinner />;
  if (orgs.isError) return <ErrorState error={orgs.error} onRetry={() => void orgs.refetch()} />;
  if (!value) return <Navigate to="/onboarding" replace />;
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrganization(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrganization() outside <OrganizationProvider>");
  return ctx;
}

/** The organization, or null outside `<OrganizationProvider>` (sign-in, onboarding): for shared UI. */
export function useOptionalOrganization(): OrgContextValue | null {
  return useContext(OrgContext);
}

/**
 * Shows an area of the portal only to people who may see it (see `SECTION_PERMISSIONS`); a deep
 * link or bookmark otherwise lands on a plain "no access" page instead of a half-broken one.
 */
export function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const { canAny, permissionsLoading } = useOrganization();
  if (permissionsLoading) return <FullPageSpinner />;
  if (!canAny(permission)) return <AccessDenied />;
  return <>{children}</>;
}

/**
 * Shows the page only while the business is active. A suspended business gets every request refused by
 * Identity, so instead of "Access denied" on every page it says why, and how to reach support (PHP-3725).
 * The business switcher and account menu around it stay usable.
 */
export function RequireActiveBusiness({ children }: { children: ReactNode }) {
  const { current } = useOrganization();
  if (current.organization.status === "suspended") {
    return <BusinessSuspended organizationName={current.organization.name} />;
  }
  return <>{children}</>;
}

export function BusinessSuspended({ organizationName }: { organizationName: string }) {
  return (
    <EmptyState
      title="This business is suspended"
      description={`${organizationName} has been suspended, so its websites, domains and billing can't be used for now. Nothing is charged while it is suspended. Contact support to find out why or to have it restored.`}
      action={
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`${organizationName} is suspended`)}`}
          className={buttonClass("secondary")}
        >
          Contact support
        </a>
      }
    />
  );
}

/**
 * The 403 page: a route the person's role doesn't cover, or any API refusal (`ErrorState` shows it for
 * PERMISSION_DENIED, e.g. a permission revoked mid-session). Retrying can't help, so it offers a way out.
 */
export function AccessDenied() {
  const org = useOptionalOrganization();
  const { pathname } = useLocation();
  const business = org?.current.organization.name;
  const roles = org?.current.roles.map((r) => r.name).join(", ");
  return (
    <EmptyState
      icon={<Lock className="size-5" aria-hidden="true" />}
      title="You don't have access to this page"
      description={
        <>
          {business && roles
            ? `Your role in ${business} (${roles}) doesn't include this area.`
            : "Your role in this business doesn't include this area."}{" "}
          If you need it, ask an Owner or Business Administrator to give you access.
        </>
      }
      action={
        pathname !== "/" && (
          <LinkButton to="/" variant="secondary">
            Go to Home
          </LinkButton>
        )
      }
    />
  );
}
